import { Injectable, inject } from '@angular/core';

import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';

import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SpinnerOverlayService } from '../service/spinner-overlay.service';

/**
 * Shows the global blocking overlay ONLY for requests the user is actively
 * waiting on -- saves, deletes, and the record being edited.
 *
 * WHY THIS CHANGED (1): the overlay used to wrap EVERY request
 * ------------------------------------------------------------
 * SpinnerOverlayService ref-counts, so on a page firing hundreds of requests
 * the overlay only lifted when the LAST one finished -- roughly 30 seconds
 * after the data had already rendered. See SILENT_GET_PATTERNS below.
 *
 * WHY THIS CHANGED (2): a hung request could pin the overlay forever
 * -------------------------------------------------------------------
 * finalize() fires on complete, error OR unsubscribe. It does NOT fire on a
 * request that simply hangs -- one that sits at "Pending" in DevTools and
 * never resolves. With no backstop, one hung request pins activeRequests
 * above zero and the overlay never clears.
 *
 * The original code had a 60s setTimeout calling spinner.reset() as a crude
 * guard against exactly this. That was removed because reset() zeroes the
 * counter while OTHER requests are still in flight, permanently desyncing it.
 *
 * The correct fix is per-request, not global: each request releases its OWN
 * spinner slot after MAX_BLOCK_MS whether or not it has finished. The request
 * is NOT cancelled -- a slow save still completes normally -- the overlay just
 * stops waiting on it. The `released` flag guarantees exactly one hide() per
 * show(), so the counter can never desync.
 */

@Injectable()
export class SpinnerInterceptor implements HttpInterceptor {

  private readonly spinner = inject(SpinnerOverlayService);

  /**
   * Longest the overlay will block on any single request.
   *
   * Nothing a user waits on should take longer than this. If a save genuinely
   * needs more time, it should show its own inline progress rather than a
   * full-screen block.
   */
  private static readonly MAX_BLOCK_MS = 15_000;

  /**
   * Background / reference-data / type-ahead traffic. The user did not ask for
   * these and should not be made to wait on them.
   *
   * No leading slash: HttpService prepends environment.apiUrl, so matching on
   * the bare path is robust regardless of how the base URL is configured.
   */
  private static readonly SILENT_GET_PATTERNS: readonly string[] = [
    // Reference data -- static for the whole session
    'dadyin-api/geo/',
    'dadyin-api/meta/',
    'dadyin-api/producttypes/',
    'dadyin-api/employee/',
    'dadyin-api/keywords',

    // Business entity configuration
    'business-entity-configuration',
    'configurations/active',

    // Type-ahead / autocomplete
    'dadyin-api/products/list/saleable/',

    // Panels that load independently of the main record
    'last-sales-invoices',
    'last-purchase-bills',
    'last-inventory-details',
    'notifications',
  ];

  /**
   * Requests that should NEVER control the global application spinner,
   * regardless of HTTP method.
   *
   * Google Maps loads its JavaScript/Places library through an Angular
   * JSONP request. JSONP is not reported as GET by HttpRequest, so it must
   * be handled separately from SILENT_GET_PATTERNS.
   */
  private static readonly SPINNER_EXCLUDED_PATTERNS: readonly string[] = [
    'maps.googleapis.com/maps/api/js',
  ];

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    /**
     * Background requests such as Google Maps must not affect the global
     * blocking spinner.
     */
    if (this.isSpinnerExcluded(req)) {
      return next.handle(req);
    }

    /**
     * Only GET requests matching the configured silent patterns are ignored.
     */
    if (this.isSilentGet(req)) {
      return next.handle(req);
    }

    // Exactly one hide() per show(), no matter which path gets there first.
    let released = false;

    const release = () => {
      if (released) {
        return;
      }

      released = true;
      this.spinner.hide();
    };

    this.spinner.show();

    /**
     * Backstop for requests that never settle.
     *
     * The request is NOT cancelled. We only release this request's spinner
     * slot so a genuinely hung request cannot keep the full-screen overlay
     * visible forever.
     */
    const watchdog = setTimeout(() => {
      console.warn(
        `[SpinnerInterceptor] Released overlay after ` +
        `${SpinnerInterceptor.MAX_BLOCK_MS}ms, request still pending: ` +
        `${req.method} ${req.urlWithParams}`
      );

      release();
    }, SpinnerInterceptor.MAX_BLOCK_MS);

    return next.handle(req).pipe(
      finalize(() => {
        clearTimeout(watchdog);
        release();
      })
    );
  }

  /**
   * Requests which should never participate in the global spinner.
   *
   * This intentionally works for ALL HTTP methods because Google Maps uses
   * JSONP rather than a normal GET request.
   */
  private isSpinnerExcluded(req: HttpRequest<any>): boolean {
    return SpinnerInterceptor.SPINNER_EXCLUDED_PATTERNS.some((pattern) =>
      req.urlWithParams.includes(pattern)
    );
  }

  /**
   * Only GETs are silent through the normal background/reference-data rules.
   * Mutations continue to block, so the user cannot double-submit a save.
   */
  private isSilentGet(req: HttpRequest<any>): boolean {
    if (req.method !== 'GET') {
      return false;
    }

    return SpinnerInterceptor.SILENT_GET_PATTERNS.some((pattern) =>
      req.url.includes(pattern)
    );
  }
}